import React, { useMemo, useState } from "react";
import {
  useGame,
  usePlayer,
  usePlayers,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { DonutChart } from "../negotiation/NegotiationDonutCharts";
import { getCounterpartCopy, isLossFrameDesign } from "../payoffFrame";
import { stageStartingMoney } from "../lossFrameMoney";
import { showsPerformanceFeedback } from "../../../shared/experimentBehavior.js";

// Keep the self-assessed contribution question code in place, but hide it for now.
const SHOW_CONTRIBUTION_QUESTION = false;
const TAKEN_SEGMENT_BACKGROUND =
  "repeating-linear-gradient(135deg, #059669 0 10px, #dc2626 10px 20px)";
const LOSER_TAKEN_SEGMENT_BACKGROUND =
  "repeating-linear-gradient(45deg, #34d399 0 10px, #dc2626 10px 20px)";
const BAR_SEGMENT_OUTLINE = "inset 0 0 0 7px rgba(17, 24, 39, 0.8)";
const SURPLUS_COLOR = "#34d399";
const DEFAULT_BASE_PAY = 1.00;

function dollars(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function seconds(value) {
  return `${Number(value || 0).toFixed(1)} seconds`;
}

function wholeSeconds(value) {
  return `${Math.round(Number(value || 0))} second`;
}

function clampMoney(value, maxValue = Infinity) {
  const number = Number(value);
  const maxNumber = Number(maxValue);
  const safeValue = Number.isFinite(number) ? number : 0;
  const safeMax = Number.isFinite(maxNumber) ? Math.max(0, maxNumber) : Infinity;

  return Math.max(0, Math.min(safeValue, safeMax));
}

function segmentFillStyle(segment) {
  if (segment.background) {
    return { background: segment.background };
  }

  return { backgroundColor: segment.color };
}

function MoneySegmentBar({
  segments,
  total = null,
  legendTextClassName = "text-xl",
  legendSwatchClassName = "h-4 w-4",
  barHeightClassName = "h-10",
  showLegendPercent = false,
  reverseLegend = false,
}) {
  const safeSegments = segments
    .map((segment) => ({
      ...segment,
      value: Math.max(0, Number(segment.value) || 0),
    }))
    .filter((segment) => segment.value > 0);
  const safeTotal = Math.max(
    0,
    Number(total) || safeSegments.reduce((sum, segment) => sum + segment.value, 0)
  );
  const legendSegments = reverseLegend ? [...safeSegments].reverse() : safeSegments;

  return (
    <div className="space-y-3">
      <div className={`flex ${barHeightClassName} overflow-hidden rounded-md bg-gray-100`}>
        {safeTotal > 0 && safeSegments.length > 0 ? (
          safeSegments.map((segment) => (
            <div
              key={segment.label}
              className="h-full"
              style={{
                width: `${Math.max(0, Math.min(100, (segment.value / safeTotal) * 100))}%`,
                ...segmentFillStyle(segment),
                boxShadow: BAR_SEGMENT_OUTLINE,
              }}
              title={`${segment.label}: ${dollars(segment.value)}`}
            />
          ))
        ) : (
          <div className="h-full w-full bg-gray-100" />
        )}
      </div>

      <div className="grid gap-2">
        {legendSegments.map((segment) => {
          const segmentValue = Math.max(0, Number(segment.value) || 0);
          const percent = safeTotal > 0
            ? segmentValue / safeTotal * 100
            : 0;
          const showNegativeValue = segment.negativeLegendValue && segmentValue > 0;
          const valueClassName = showNegativeValue
            ? "text-red-700"
            : segment.legendValueClassName || "text-gray-900";
          const valuePrefix = showNegativeValue ? "-" : "";

          return (
            <div
              key={segment.label}
              className={`grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-3 ${legendTextClassName}`}
            >
              <span
                className={`${legendSwatchClassName} rounded-sm`}
                style={segmentFillStyle(segment)}
              />
              <span className="text-gray-700">{segment.label}</span>
              <span className={`grid grid-cols-[7.5rem_6.5rem] justify-end text-right ${valueClassName}`}>
                <span className="font-semibold tabular-nums">
                  {valuePrefix}{dollars(segment.value)}
                </span>
                {showLegendPercent ? (
                  <span className="font-normal tabular-nums">
                    ({percent.toFixed(1)}%)
                  </span>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LossSavedCard({
  title,
  savedDollars,
  lostDollars,
  inCircleSeconds,
  durationSeconds,
}) {
  const totalAtStake = savedDollars + lostDollars;
  const savedPercent = totalAtStake > 0 ? (savedDollars / totalAtStake) * 100 : 0;

  return (
    <div className="rounded-md border border-gray-200 bg-white p-5 space-y-4">
      <div>
        <div className="text-3xl font-semibold text-gray-900">{title}</div>
        <div className="mt-1 text-xl text-gray-600">
          Cursor in circle for <strong>{Number(inCircleSeconds || 0).toFixed(1)}</strong>
          /{Number(durationSeconds || 0).toFixed(0)} seconds
        </div>
      </div>

      <MoneySegmentBar
        total={totalAtStake}
        legendTextClassName="text-2xl"
        legendSwatchClassName="h-5 w-5"
        showLegendPercent
        segments={[
          { label: "Lost", value: lostDollars, color: "#dc2626" },
          { label: "Saved", value: savedDollars, color: "#059669" },
        ]}
      />
    </div>
  );
}

function ContributionCard({
  title,
  titleSuffix,
  dollarsValue,
  secondsValue,
  totalSecondsValue,
  percentValue,
  colorClassName,
  barClassName,
}) {
  const safePercent = Math.max(0, Math.min(100, Number(percentValue) || 0));

  return (
    <div className="rounded-md border border-gray-200 bg-white p-4 space-y-3">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 gap-y-1">
        <div className="col-span-2 whitespace-nowrap text-2xl text-gray-900">
          <span className="font-semibold">{title}</span>
          {titleSuffix ? ` ${titleSuffix}` : null}
        </div>
        <div className={`text-3xl font-semibold ${colorClassName}`}>
          {dollars(dollarsValue)}
        </div>
        <div className="whitespace-nowrap text-right text-2xl text-gray-900">
          <span className={`font-semibold ${colorClassName}`}>
            {Number(secondsValue || 0).toFixed(1)}
          </span>
          /{Number(totalSecondsValue || 0).toFixed(0)} seconds
        </div>
      </div>
      <div className="h-4 overflow-hidden rounded bg-gray-100">
        <div
          className={`h-full rounded ${barClassName}`}
          style={{ width: `${safePercent}%` }}
        />
      </div>
    </div>
  );
}

function SectionHeader({ title, children }) {
  return (
    <div>
      <h2 className="text-4xl font-semibold text-gray-900">{title}</h2>
      {children ? <p className="mt-1 text-2xl text-gray-600">{children}</p> : null}
    </div>
  );
}

export function Stage1ResultStage() {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const stage1 = game.get("stage1");
  const design = game.get("design") || {};
  const lossFrame = isLossFrameDesign(design);
  const counterpart = getCounterpartCopy(design);
  const partner = players.find((p) => p.id !== player.id);

  const [showSlider, setShowSlider] = useState(
    player.stage.get("stage1ContributionSlider") != null
  );
  const [sliderValue, setSliderValue] = useState(
    player.stage.get("stage1ContributionSlider") ?? 50
  );
  const [sliderTouched, setSliderTouched] = useState(
    player.stage.get("stage1ContributionSlider") != null
  );

  const allSubmitted = useMemo(
    () => players.every((p) => p.stage.get("submit")),
    [players]
  );

  function handleContinue() {
    if (!SHOW_CONTRIBUTION_QUESTION) {
      handleSubmit();
      return;
    }

    setShowSlider(true);
  }

  function handleSliderChange(e) {
    const val = Number(e.target.value);
    setSliderValue(val);
    setSliderTouched(true);
    player.stage.set("stage1ContributionSlider", val);
  }

  function handleSubmit() {
    player.stage.set("submit", true);
  }

  if (!stage1) {
    return (
      <div className="max-w-3xl mx-auto px-8 py-6 text-center text-2xl text-gray-500">
        Computing tracking task results...
      </div>
    );
  }

  const youWon = stage1.winnerId === player.id;
  const teamWinnings = Number(stage1.teamWinnings ?? 0);
  const storedYourEarnings = Number(player.get("stage1Earnings"));
  const storedPartnerEarnings = Number(partner?.get("stage1Earnings"));
  const yourEarnings = Number.isFinite(storedYourEarnings)
    ? storedYourEarnings
    : youWon
    ? teamWinnings
    : 0;
  const partnerEarnings = Number.isFinite(storedPartnerEarnings)
    ? storedPartnerEarnings
    : !youWon
    ? teamWinnings
    : 0;
  const showPerformanceFeedback = showsPerformanceFeedback(design);

  if (!showPerformanceFeedback) {
    return (
      <div className="w-full max-w-4xl mx-auto px-8 py-8 space-y-6">
        <SectionHeader title="Game 1 Outcome" />

        <div
          className={`rounded-md border p-6 text-3xl ${
            youWon
              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
              : "border-red-300 bg-red-50 text-red-900"
          }`}
        >
          <p className="font-semibold">
            {youWon ? "You won Game 1." : `Your ${counterpart.term} won Game 1.`}
          </p>
          <p className="mt-3 text-2xl">
            The winner receives the entire Game 1 pot.
          </p>
          {stage1.tied ? (
            <p className="mt-3 text-2xl font-semibold">
              The result was tied, so the winner was selected by random draw.
            </p>
          ) : null}
        </div>

        <DonutChart
          title="Game 1 Pay"
          legendSide
          chartClassName="w-56 h-56"
          centerValueClassName="fill-blue-700 text-sm font-semibold"
          centerTotalClassName="fill-gray-900 text-sm font-semibold"
          centerLabelClassName="fill-gray-500 text-[10px]"
          legendTextClassName="text-3xl"
          showZeroValueLegendItems
          centerValue={yourEarnings}
          centerTotal={teamWinnings}
          segments={[
            { label: "Your pay", value: yourEarnings, color: "#4f46e5" },
            { label: `${counterpart.capitalizedTerm} pay`, value: partnerEarnings, color: "#dc2626" },
          ]}
        />

        <div className="flex items-center space-x-4">
          <Button
            handleClick={handleContinue}
            autoFocus
            disabled={player.stage.get("submit")}
            className="text-xl"
          >
            Continue
          </Button>
          {player.stage.get("submit") && !allSubmitted ? (
            <span className="text-lg text-gray-500">
              Waiting for {counterpart.term}...
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  const teamInCircleSeconds = Number(stage1.teamInCircleSeconds ?? 0);
  const gameDurationSeconds = Number(stage1.durationSeconds ?? teamInCircleSeconds);
  const scoresByPlayerId = Object.fromEntries(
    (stage1.scores || []).map((score) => [
      score.playerId,
      Number(score.inCircleSeconds ?? 0),
    ])
  );
  const yourInCircleSeconds = scoresByPlayerId[player.id] ?? 0;
  const partnerInCircleSeconds = partner
    ? scoresByPlayerId[partner.id] ?? 0
    : 0;
  const yourContributionPercent =
    gameDurationSeconds > 0 ? (yourInCircleSeconds / gameDurationSeconds) * 100 : 0;
  const partnerContributionPercent =
    gameDurationSeconds > 0 ? (partnerInCircleSeconds / gameDurationSeconds) * 100 : 0;
  const yourContributionDollars =
    teamInCircleSeconds > 0 ? teamWinnings * (yourInCircleSeconds / teamInCircleSeconds) : 0;
  const partnerContributionDollars =
    teamInCircleSeconds > 0 ? teamWinnings * (partnerInCircleSeconds / teamInCircleSeconds) : 0;
  const trackingDollarsPerSecond = Math.max(
    0,
    Number(stage1.trackingDollarsPerSecond ?? design.trackingDollarsPerSecond ?? 0)
  );
  const stage1StakeDollars = Math.max(
    0,
    Number(
      stage1.stage1MaxPerParticipant ??
        design.stage1MaxPerParticipant ??
        gameDurationSeconds * trackingDollarsPerSecond
    ) || 0
  );
  const stage2NotAtStakeDollars = stageStartingMoney(design, 2);
  const yourSavedDollars = clampMoney(
    yourInCircleSeconds * trackingDollarsPerSecond,
    stage1StakeDollars
  );
  const partnerSavedDollars = clampMoney(
    partnerInCircleSeconds * trackingDollarsPerSecond,
    stage1StakeDollars
  );
  const yourLostDollars = clampMoney(
    stage1StakeDollars - yourSavedDollars,
    stage1StakeDollars
  );
  const partnerLostDollars = clampMoney(
    stage1StakeDollars - partnerSavedDollars,
    stage1StakeDollars
  );
  const basePayRaw = Number(design.basePay ?? design.showUpPayment ?? DEFAULT_BASE_PAY);
  const basePayDollars = Math.max(
    0,
    Number.isFinite(basePayRaw) ? basePayRaw : DEFAULT_BASE_PAY
  );
  const yourSavedKeptDollars = youWon ? yourSavedDollars : 0;
  const takenFromOpponentDollars = youWon ? partnerSavedDollars : 0;
  const takenByOpponentDollars = youWon ? 0 : yourSavedDollars;
  const game1MoneyLeftDollars = youWon
    ? yourSavedDollars + partnerSavedDollars
    : 0;
  const opponentSavedKeptDollars = youWon ? 0 : partnerSavedDollars;
  const takenFromYouDollars = youWon ? 0 : yourSavedDollars;
  const takenByYouDollars = youWon ? partnerSavedDollars : 0;
  const opponentGame1MoneyLeftDollars = youWon
    ? 0
    : partnerSavedDollars + yourSavedDollars;
  const takenFromOpponentOffsetDollars = Math.min(
    takenFromOpponentDollars,
    yourLostDollars
  );
  const surplusFromOpponentDollars = Math.max(
    0,
    takenFromOpponentDollars - yourLostDollars
  );
  const takenFromYouOffsetDollars = Math.min(
    takenFromYouDollars,
    partnerLostDollars
  );
  const opponentSurplusFromYouDollars = Math.max(
    0,
    takenFromYouDollars - partnerLostDollars
  );
  const yourNetLostDollars =
    Math.max(0, yourLostDollars - takenFromOpponentOffsetDollars) +
    takenByOpponentDollars;
  const opponentNetLostDollars =
    Math.max(0, partnerLostDollars - takenFromYouOffsetDollars) +
    takenByYouDollars;
  const currentMoneyLeftDollars =
    basePayDollars + game1MoneyLeftDollars + stage2NotAtStakeDollars;
  const opponentCurrentMoneyLeftDollars =
    basePayDollars + opponentGame1MoneyLeftDollars + stage2NotAtStakeDollars;
  const moneyBarTotalDollars =
    stage1StakeDollars + stage2NotAtStakeDollars + surplusFromOpponentDollars;
  const opponentMoneyBarTotalDollars =
    stage1StakeDollars + stage2NotAtStakeDollars + opponentSurplusFromYouDollars;

  if (lossFrame) {
    return (
      <div className="max-w-5xl mx-auto px-8 py-6 space-y-7">
        <SectionHeader title="Game 1 Outcome">
          {/* Game 1 is shown as money lost, money saved, and saved money that was
          taken by the player who stayed in the circle longer. */}
        </SectionHeader>

        <section className="rounded-md border border-gray-200 bg-black-100 p-5 space-y-5">
          <SectionHeader title="Performance">
            {/* You had <strong>{dollars(stage1StakeDollars)}</strong> at stake in Game 1. */}
          </SectionHeader>

          <div className="grid lg:grid-cols-2 gap-4">
            <LossSavedCard
              title="You"
              savedDollars={yourSavedDollars}
              lostDollars={yourLostDollars}
              inCircleSeconds={yourInCircleSeconds}
              durationSeconds={gameDurationSeconds}
            />
            <LossSavedCard
              title={counterpart.capitalizedTerm}
              savedDollars={partnerSavedDollars}
              lostDollars={partnerLostDollars}
              inCircleSeconds={partnerInCircleSeconds}
              durationSeconds={gameDurationSeconds}
            />
          </div>
        </section>

        <div
          className={`rounded-md border p-5 text-3xl ${
            youWon
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-red-200 bg-red-50 text-red-900"
          }`}
        >
          {youWon ? (
            <div>
              You <b>saved more money</b> than your {counterpart.term}, so <b>the money they saved went to you</b>.
              {surplusFromOpponentDollars > 0 ? (
                <>
                  <br />
                  <br />
                  After replacing the money you lost,{" "}
                  <strong>{dollars(surplusFromOpponentDollars)}</strong> of
                  your {counterpart.term}&apos;s saved money is surplus, so you have more
                  from Game 1 than you started with.
                </>
              ) : null}
            </div>
          ) : (
            <div>
              <strong>You lost all the game 1 money ({dollars(stage1StakeDollars)}).</strong>
              <br />
              <br />

              Your {counterpart.term} saved more, so they <strong>took the money you saved</strong>.
              <br />
              <br />

              You have <strong>{dollars(game1MoneyLeftDollars)}</strong> left from Game 1.
            </div>
          )}
          {stage1.tied ? (
            <div className="mt-2 font-semibold">
              Scores were tied, so the winner was selected by random draw.
            </div>
          ) : null}
        </div>

        <section className="rounded-md border border-gray-200 bg-white p-5 space-y-5">
          <SectionHeader title="Your Money Lost">
          </SectionHeader>

          <div className="rounded-md border border-gray-200 p-5 space-y-4">
            <div>
              {/* <div className="text-2xl font-semibold text-gray-900">
                Money lost
              </div> */}
              <div
                className={`mt-1 text-5xl font-semibold ${
                  surplusFromOpponentDollars > 0
                    ? "text-emerald-700"
                    : "text-red-700"
                }`}
              >
                {surplusFromOpponentDollars > 0
                  ? `+${dollars(surplusFromOpponentDollars)}`
                  : `-${dollars(yourNetLostDollars)}`}
              </div>
            </div>

            <MoneySegmentBar
              total={moneyBarTotalDollars}
              legendTextClassName="text-3xl"
              legendSwatchClassName="h-6 w-6"
              barHeightClassName="h-16"
              showLegendPercent
              segments={[
                ...(surplusFromOpponentDollars > 0
                  ? [
                      {
                        label: `Surplus, from ${counterpart.term}`,
                        value: surplusFromOpponentDollars,
                        color: SURPLUS_COLOR,
                        legendValueClassName: "text-emerald-700",
                      },
                    ]
                  : []),
                {
                  label: "Lost",
                  value: Math.max(
                    0,
                    yourLostDollars - takenFromOpponentOffsetDollars
                  ),
                  color: "#dc2626",
                  negativeLegendValue: true,
                },
                ...(takenFromOpponentOffsetDollars > 0
                  ? [
                      {
                        label: `Saved, from ${counterpart.term}`,
                        value: takenFromOpponentOffsetDollars,
                        background: TAKEN_SEGMENT_BACKGROUND,
                        legendValueClassName: "text-emerald-700",
                      },
                    ]
                  : []),
                ...(takenByOpponentDollars > 0
                  ? [
                      {
                        label: "Lost, taken from you",
                        value: takenByOpponentDollars,
                        background: LOSER_TAKEN_SEGMENT_BACKGROUND,
                        negativeLegendValue: true,
                      },
                    ]
                  : []),
                ...(yourSavedKeptDollars > 0
                  ? [
                      {
                        label: "Saved",
                        value: yourSavedKeptDollars,
                        color: "#059669",
                        legendValueClassName: "text-emerald-700",
                      },
                    ]
                  : []),
                {
                  label: "At Stake Next",
                  value: stage2NotAtStakeDollars,
                  color: "#9ca3af",
                },
              ]}
            />

            <div className="text-4xl text-gray-700">
              You have{" "}
              <strong className="text-gray-900">
                {dollars(currentMoneyLeftDollars)}
              </strong>{" "}
              bonus pay left.
            </div>

            {surplusFromOpponentDollars > 0 ? (
              <div className="text-2xl text-amber-700">
                This includes <strong>{dollars(surplusFromOpponentDollars)}</strong>{" "}
                more from Game 1 than you started with.
              </div>
            ) : null}

            <div className="text-2xl text-gray-700">
              Base pay is {dollars(basePayDollars)}.
            </div>
          </div>
        </section>

        <section className="rounded-md border border-gray-200 bg-white p-5 space-y-5">
          <SectionHeader title={`${counterpart.capitalizedTerm} Money Lost`}>
          </SectionHeader>

          <div className="rounded-md border border-gray-200 p-5 space-y-4">
            <div>
              <div
                className={`mt-1 text-5xl font-semibold ${
                  opponentSurplusFromYouDollars > 0
                    ? "text-emerald-700"
                    : "text-red-700"
                }`}
              >
                {opponentSurplusFromYouDollars > 0
                  ? `+${dollars(opponentSurplusFromYouDollars)}`
                  : `-${dollars(opponentNetLostDollars)}`}
              </div>
            </div>

            <MoneySegmentBar
              total={opponentMoneyBarTotalDollars}
              legendTextClassName="text-3xl"
              legendSwatchClassName="h-6 w-6"
              barHeightClassName="h-16"
              showLegendPercent
              segments={[
                ...(opponentSurplusFromYouDollars > 0
                  ? [
                      {
                        label: "Surplus, taken from you",
                        value: opponentSurplusFromYouDollars,
                        color: SURPLUS_COLOR,
                        legendValueClassName: "text-emerald-700",
                      },
                    ]
                  : []),
                {
                  label: "Lost",
                  value: Math.max(
                    0,
                    partnerLostDollars - takenFromYouOffsetDollars
                  ),
                  color: "#dc2626",
                  negativeLegendValue: true,
                },
                ...(takenByYouDollars > 0
                  ? [
                      {
                        label: "Lost, to you",
                        value: takenByYouDollars,
                        background: LOSER_TAKEN_SEGMENT_BACKGROUND,
                        negativeLegendValue: true,
                      },
                    ]
                  : []),
                ...(takenFromYouOffsetDollars > 0
                  ? [
                      {
                        label: "Saved, taken from you",
                        value: takenFromYouOffsetDollars,
                        background: TAKEN_SEGMENT_BACKGROUND,
                        legendValueClassName: "text-emerald-700",
                      },
                    ]
                  : []),
                ...(opponentSavedKeptDollars > 0
                  ? [
                      {
                        label: "Saved",
                        value: opponentSavedKeptDollars,
                        color: "#059669",
                        legendValueClassName: "text-emerald-700",
                      },
                    ]
                  : []),
                {
                  label: "At Stake Next",
                  value: stage2NotAtStakeDollars,
                  color: "#9ca3af",
                },
              ]}
            />

            <div className="text-4xl text-gray-700">
              {counterpart.capitalizedTerm} has{" "}
              <strong className="text-gray-900">
                {dollars(opponentCurrentMoneyLeftDollars)}
              </strong>{" "}
              bonus pay left.
            </div>

            {opponentSurplusFromYouDollars > 0 ? (
              <div className="text-2xl text-amber-700">
                This includes{" "}
                <strong>{dollars(opponentSurplusFromYouDollars)}</strong> more
                from Game 1 than they started with.
              </div>
            ) : null}
          </div>
        </section>

        <div className="flex items-center space-x-4">
          <Button
            handleClick={handleContinue}
            autoFocus
            disabled={player.stage.get("submit")}
            className="text-xl"
          >
            Continue
          </Button>
          {player.stage.get("submit") && !allSubmitted ? (
            <span className="text-lg text-gray-500">
              Waiting for {counterpart.term}...
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-8 py-6 space-y-6">
      <SectionHeader title="Game 1 Outcome" />

      <div className="rounded-md border border-gray-200 bg-gray-50 p-5 space-y-4">
        <p className="text-2xl text-gray-600">
          During the <strong>{wholeSeconds(gameDurationSeconds)}</strong> game, your team{" "}
          {lossFrame ? "saved" : "earned"} <strong>{dollars(teamWinnings)}</strong>{" "}
          {lossFrame ? "from being lost" : "from"}{" "}
          a combined <strong>{seconds(teamInCircleSeconds)}</strong> with your cursors in the circle.
        </p>

        <div className="grid lg:grid-cols-2 gap-4">
          <ContributionCard
            title="Your contribution"
            titleSuffix={lossFrame ? "saved from loss" : "to team pot"}
            dollarsValue={yourContributionDollars}
            secondsValue={yourInCircleSeconds}
            totalSecondsValue={gameDurationSeconds}
            percentValue={yourContributionPercent}
            colorClassName="text-blue-700"
            barClassName="bg-blue-600"
          />
          <ContributionCard
            title={`${counterpart.capitalizedTerm} contribution`}
            titleSuffix={lossFrame ? "saved from loss" : "to team pot"}
            dollarsValue={partnerContributionDollars}
            secondsValue={partnerInCircleSeconds}
            totalSecondsValue={gameDurationSeconds}
            percentValue={partnerContributionPercent}
            colorClassName="text-red-700"
            barClassName="bg-red-600"
          />
        </div>
      </div>

      <div
        className={`rounded-md border p-5 text-2xl ${
          youWon
            ? "border-emerald-200 bg-emerald-50 text-emerald-900"
            : "border-red-200 bg-red-50 text-red-900"
        }`}
      >
        {youWon ? (
          <div>
            <b>You</b> kept your cursor in the circle <b>longer</b>, so you{" "}
            {lossFrame ? (
              <>
                took <b>all the money your team saved from being lost</b>.
              </>
            ) : (
              <>
                received <b>all the pay</b>.
              </>
            )}
          </div>
        ) : (
          <div>
            <b>Your {counterpart.term}</b> kept their cursor in the circle <b>longer</b>, so they{" "}
            {lossFrame ? (
              <>
                took <b>all the money your team saved from being lost</b>.
              </>
            ) : (
              <>
                received <b>all the pay</b>.
              </>
            )}
          </div>
        )}
        {stage1.tied ? (
          <div className="mt-2 font-semibold">
            Scores were tied, so the winner was selected by random draw.
          </div>
        ) : null}
      </div>

      <DonutChart
        title={lossFrame ? "Game 1 Money Kept" : "Game 1 Pay"}
        legendSide
        chartClassName="w-56 h-56"
        centerValueClassName="fill-blue-700 text-sm font-semibold"
        centerTotalClassName="fill-gray-900 text-sm font-semibold"
        centerLabelClassName="fill-gray-500 text-[10px]"
        legendTextClassName="text-3xl"
        showZeroValueLegendItems
        centerValue={yourEarnings}
        centerTotal={teamWinnings}
        segments={[
          { label: lossFrame ? "Your money kept" : "Your pay", value: yourEarnings, color: "#4f46e5" },
          { label: `${counterpart.capitalizedTerm} ${lossFrame ? "money kept" : "pay"}`, value: partnerEarnings, color: "#dc2626" },
        ]}
      />

      {!SHOW_CONTRIBUTION_QUESTION || !showSlider ? (
        <div className="flex items-center space-x-4">
          <Button
            handleClick={handleContinue}
            autoFocus
            disabled={player.stage.get("submit")}
            className="text-xl"
          >
            Continue
          </Button>
          {player.stage.get("submit") && !allSubmitted ? (
            <span className="text-lg text-gray-500">
              Waiting for {counterpart.term}...
            </span>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border border-gray-200 p-5 space-y-3 text-xl text-gray-700">
            <p className="font-medium text-gray-900">
              What proportion of the {dollars(teamWinnings)} team {lossFrame ? "saved money" : "winnings"} do you think was your contribution?
            </p>
            <p className="text-sm text-gray-500">
              Having your mouse inside the circle the entire time would be {stage1.durationSeconds} seconds.
            </p>
            <div className="flex items-center space-x-3">
              <span className="w-12 text-right font-medium text-base">0%</span>
              <input
                type="range"
                min={0}
                max={100}
                value={sliderValue}
                onChange={handleSliderChange}
                style={{ "--val": sliderValue }}
                className="flex-1 filled-slider"
              />
              <span className="w-12 font-medium text-base">100%</span>
            </div>
            <div className="text-center text-lg text-gray-600">
              Your contribution: <span className="font-bold">{sliderValue}%</span>{" "}
              <span className="font-bold">{seconds(teamInCircleSeconds * sliderValue / 100)}</span>,{" "}
              <span className="font-bold">{dollars(teamWinnings * sliderValue / 100)}</span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <Button
              handleClick={handleSubmit}
              autoFocus
              disabled={!sliderTouched || player.stage.get("submit")}
              className="text-xl"
            >
              Continue
            </Button>
            {player.stage.get("submit") && !allSubmitted ? (
              <span className="text-lg text-gray-500">
                Waiting for {counterpart.term}...
              </span>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
